import { Icon, Popup, Table } from 'semantic-ui-react';
import styled from '@emotion/styled';
import { RHFormCheckbox, RHFormNumberInput } from 'components';
import { useFormContext } from 'react-hook-form';
import { AddRoomBookingFormValues } from './validation';
import { LrwRoomClashDto, LrwRoomDto, OnComputerWritingFormat } from 'api/planning';

type Props = {
  room: LrwRoomDto;
  id: number;
  showClashes: boolean;
};

export const AvailableRoom = ({ room, id, showClashes }: Props) => {
  const { isChecked, control } = useSection(id);

  return (
    <Table.Row key={id} data-testid={`available-room-row-lrw-${room.id}`}>
      <Table.Cell>
        <S.Centered>
          <RHFormCheckbox
            name={`rooms.${id}.isChecked`}
            control={control}
            disabled={room.clashes?.length > 0}
            data-testid="room-selection-checkbox-lrw"
          />
        </S.Centered>
      </Table.Cell>
      {showClashes ? (
        <Table.Cell>
          <S.Centered>
            {room.clashes?.length > 0 ? (
              <Popup
                hoverable
                position="right center"
                trigger={
                  <S.WarningIcon>
                    <Icon color="orange" name="exclamation triangle" />
                  </S.WarningIcon>
                }
                content={
                  <S.PopupContent>
                    <S.Title>Existing sessions</S.Title>
                    {room.clashes.map((clash, index) => {
                      const { product, writingFormat, component } = getClashedExamDetails(clash);

                      return (
                        <S.ClashItem
                          key={`${clash.startTime}-${clash.endTime}-${clash.productName}-${index}`}
                        >
                          <S.Time>
                            <Icon name="clock outline" size="large" />
                            <span>
                              {formatClashTime(clash.startTime)} - {formatClashTime(clash.endTime)}
                            </span>
                          </S.Time>
                          {details('Product', product)}
                          {details('Format', writingFormat)}
                          {details('Component', component)}
                        </S.ClashItem>
                      );
                    })}
                  </S.PopupContent>
                }
              />
            ) : (
              ''
            )}
          </S.Centered>
        </Table.Cell>
      ) : null}
      <Table.Cell data-testid="room-building-cell-lrw">{room.buildingName}</Table.Cell>
      <Table.Cell data-testid="room-floor-cell-lrw">{room.floor}</Table.Cell>
      <Table.Cell data-testid="room-name-cell-lrw">{room.name}</Table.Cell>
      <Table.Cell data-testid="room-seat-limit-cell-lrw">{room.totalSeats}</Table.Cell>
      <Table.Cell data-testid="room-planned-seats-cell-lrw">
        <S.Centered>
          {isChecked === true ? (
            <RHFormNumberInput
              name={`rooms.${id}.seatLimit`}
              min={1}
              max={room.maxSeatLimit}
              defaultValue={room.maxSeatLimit}
              size="small"
              width="10"
              control={control}
            />
          ) : (
            '-'
          )}
        </S.Centered>
      </Table.Cell>
    </Table.Row>
  );
};

const useSection = (id: number) => {
  const { control, watch } = useFormContext<AddRoomBookingFormValues>();

  const roomId = watch(`rooms.${id}.roomId`);
  const isChecked = watch(`rooms.${id}.isChecked`);

  return {
    roomId,
    isChecked,
    control,
  };
};

const formatClashTime = (time: string) => {
  const match = /(\d{2}):(\d{2})/.exec(time);
  if (!match) {
    return time;
  }

  return `${match[1]}:${match[2]}`;
};

const details = (property: string, value: string) => {
  return (
    <div>
      {property}: <S.Value>{value}</S.Value>
    </div>
  );
};

const mapFormat = (format: OnComputerWritingFormat) => {
  switch (format) {
    case OnComputerWritingFormat.OnComputer:
      return 'On computer';
    case OnComputerWritingFormat.OnPaper:
      return 'On paper';
    default:
      return String(format);
  }
};

const getClashedExamDetails = (clash: LrwRoomClashDto) => {
  if (!clash.isIeltsExam) {
    return {
      product: 'Not IELTS',
      writingFormat: '-',
      component: '-',
    };
  }

  return {
    product: clash.productName ?? '-',
    writingFormat: clash.format ? mapFormat(clash.format) : '-',
    component: clash.component ?? '-',
  };
};

const S = {
  Centered: styled.div`
    display: flex;
    justify-content: center;
  `,
  WarningIcon: styled.span`
    cursor: pointer;
  `,
  PopupContent: styled.div`
    display: flex;
    flex-direction: column;
    width: 200px;
  `,
  Title: styled.div`
    margin: 0;
    font-weight: 700;
    font-size: 12px;
    color: orange;
    display: block;
    padding-bottom: 5px;
  `,
  Time: styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 700;
    margin-bottom: 12px;
  `,
  Value: styled.span`
    font-weight: 700;
  `,
  ClashItem: styled.div`
    display: flex;
    flex-direction: column;
    border-top: 1px solid #e2e2e3;
    font-size: 12px;
    padding-top: 10px;
    padding-bottom: 10px;
  `,
  NumberInput: styled(RHFormNumberInput)`
    display: flex;
    justify-content: center;
    width: 40px;
  `,
};
